import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Map as MapIcon, ScanSearch } from "lucide-react";
import FieldMapRow from "@/components/mongo/FieldMapRow";
import FieldMapPublishBar from "@/components/mongo/FieldMapPublishBar";

const FILTERS = ["all", "mapped", "shared", "hidden"];
const TAB = "px-2.5 py-1 rounded text-[11px] transition-colors";

export default function FieldMapTable({ onChanged }) {
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("all");
    const [dbTab, setDbTab] = useState("all");
    const [via, setVia] = useState("");
    const load = useCallback(async () => {
        try { setData((await api.get("/admin/mongo-mcp/field-map")).data); }
        catch (e) { toast.error(formatApiErrorDetail(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);
    const scanning = !!data?.scan?.running;
    useEffect(() => {
        if (!scanning) return undefined;
        const t = setInterval(load, 1500);
        return () => clearInterval(t);
    }, [scanning, load]);

    const scan = async () => {
        try { await api.post(`/admin/mongo-mcp/field-map/scan${via ? `?server_id=${encodeURIComponent(via)}` : ""}`); toast("Scanning collections for company fields…"); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e)); }
    };
    const saved = () => { load(); onChanged?.(); };
    const readOnly = !!data?.read_only;
    const items = (data?.items || []).filter((r) => (filter === "all" || r.status === filter) && (dbTab === "all" || r.database === dbTab));
    const perDb = (d) => (data?.items || []).filter((r) => r.database === d);
    return (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-md" data-testid="field-map-card">
            <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-white/10">
                <MapIcon className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.5} />
                <div className="flex-1 min-w-[240px]">
                    <div className="font-display text-lg tracking-tight">Company field map · every environment</div>
                    <div className="text-[11px] text-white/45">Tenants only see collections with a confirmed company field (or marked shared). New collections stay hidden.</div>
                </div>
                {!readOnly && (
                    <div className="flex items-center gap-1.5">
                        <select value={via} onChange={(e) => setVia(e.target.value)} disabled={scanning} data-testid="field-map-scan-via"
                            className="bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[11px] font-mono focus:outline-none">
                            <option value="">scan via default</option>
                            {(data?.scan_servers || []).map((s) => <option key={s.id} value={s.id}>via {s.label}</option>)}
                        </select>
                        <button onClick={scan} disabled={!data?.scan_servers?.length || scanning} data-testid="field-map-scan"
                            className="px-3 py-2 rounded-md text-xs font-medium border border-white/15 hover:bg-white/5 disabled:opacity-40 flex items-center gap-1.5 transition-colors">
                            <ScanSearch className="w-3.5 h-3.5" /> {scanning ? `Scanning ${data.scan.done}/${data.scan.total || "…"}` : "Scan collections"}
                        </button>
                    </div>
                )}
            </div>
            {data && <FieldMapPublishBar data={data} onPublished={saved} />}
            <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-2.5 border-b border-white/10">
                <div className="flex gap-1" data-testid="field-map-db-tabs">
                    {["all", ...(data?.databases || [])].map((d) => (
                        <button key={d} onClick={() => setDbTab(d)} data-testid={`field-map-db-${d}`}
                            className={`${TAB} font-mono ${dbTab === d ? "bg-[#7C3AED] text-white" : "text-white/55 hover:bg-white/5"}`}>
                            {d === "all" ? "all databases" : d}{d !== "all" && data ? ` ${perDb(d).length}` : ""}
                            {d !== "all" && perDb(d).some((r) => r.pending) ? " •" : ""}
                        </button>
                    ))}
                </div>
                <div className="flex gap-1" data-testid="field-map-filters">
                    {FILTERS.map((f) => (
                        <button key={f} onClick={() => setFilter(f)} data-testid={`field-map-filter-${f}`}
                            className={`${TAB} capitalize ${filter === f ? "bg-white text-black" : "text-white/55 hover:bg-white/5"}`}>
                            {f}{data?.counts && f !== "all" ? ` ${data.counts[f]}` : ""}
                        </button>
                    ))}
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left min-w-[760px]" data-testid="field-map-table">
                    <thead>
                        <tr className="label-xs text-white/40 border-b border-white/10">
                            {["Database", "Collection", "Suggested field", "Company field", "Shared", "Status", ""].map((h) => <th key={h} className="px-4 py-2 font-normal">{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {!data ? <EmptyRow text="Loading…" /> : items.length === 0 ? <EmptyRow text={data.items.length ? "No collection matches this filter." : readOnly ? "The hub has not published a field map yet." : "No collections yet — run a scan."} /> :
                            items.map((r) => <FieldMapRow key={`${r.database}.${r.collection}.${r.confirmed_at || ""}`} row={r} candidates={data.candidates} onSaved={saved} readOnly={readOnly} />)}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function EmptyRow({ text }) {
    return <tr><td colSpan={7} className="px-4 py-8 text-center text-xs text-white/40" data-testid="field-map-empty">{text}</td></tr>;
}
